import React from "react";
import { CRMColors } from "../../../models/CRMColors";
import { CRMSpacing } from "../../../models/CRMSpacing";
import { Background } from "../../BuildingBlocks/Background";
import { BackgroundColour } from "../../BuildingBlocks/BackgroundColour";
import { MinHeight } from "../../BuildingBlocks/MinHeight";
import { SpacingColumn } from "../../BuildingBlocks/SpacingColumn";
import { CRMParagraph } from "../../Simple/CRMParagraph/CRMParagraph";
import { WeightBold } from "../../WeightBold/WeightBold";
import CRMInputGeneralComponent from "../../CRMInputs/CRMInputGeneralComponent/CRMInputGeneralComponent";
import { TEmailTemplateForm } from "../../../../../domain/codecs/form/EmailTemplatesForm";
import { EmailTemplateTokens } from "../../../../../domain/emailTemplates/tokens";
import { CRMEmailTemplateRow } from "./CRMEmailTemplateRow";

const matchesSearch = (form: TEmailTemplateForm, search: string): boolean =>
    `${form.edited.recipient} ${form.edited.acting_for} ${form.edited.use_case}`
        .toLowerCase()
        .includes(search.toLowerCase());

export const CRMEmailTemplatesBlock = (props: React.PropsWithChildren<{
    templateForms: Array<TEmailTemplateForm>,
    onChange: (form: TEmailTemplateForm) => void,
    onSave: (form: TEmailTemplateForm) => void,
}>): JSX.Element => {
    const [search, setSearch] = React.useState("");
    const [openId, setOpenId] = React.useState<string | null>(null);

    const visibleForms = props.templateForms.filter((form) => matchesSearch(form, search));

    return (
        <BackgroundColour colour={CRMColors.NEUTRAL_PAPER}>
            <MinHeight height={"700px"}>
                <Background padding={CRMSpacing.LARGE}>
                    <SpacingColumn spacing={CRMSpacing.LARGE}>
                        <CRMParagraph>
                            The templates offered in the compose-email window. Editing one here changes
                            what every case handler sends from their next email onwards.
                        </CRMParagraph>

                        <CRMParagraph>
                            <WeightBold>Fields you can use in a body: </WeightBold>
                            {Object.entries(EmailTemplateTokens)
                                .map(([name, token]) => `{{${name}}} (${token.label.toLowerCase()})`)
                                .join(", ")}
                            . Anything we do not hold for a case is left as a visible marker, and an
                            email still carrying one cannot be sent.
                        </CRMParagraph>

                        <CRMInputGeneralComponent
                            value={search}
                            inputType="text"
                            placeholder="Search by recipient, who we act for, or use case"
                            onChange={setSearch}
                            displayError={false}
                        />

                        {visibleForms.length === 0 &&
                            <CRMParagraph>
                                {props.templateForms.length === 0
                                    ? "No templates have been loaded into the database yet."
                                    : "No templates match that search."
                                }
                            </CRMParagraph>
                        }

                        <SpacingColumn spacing={CRMSpacing.SMALL}>
                            {visibleForms.map((form) =>
                                <CRMEmailTemplateRow
                                    key={form.edited.id}
                                    form={form}
                                    isOpen={openId === form.edited.id}
                                    onToggle={() => setOpenId(openId === form.edited.id ? null : form.edited.id)}
                                    onChange={props.onChange}
                                    onSave={props.onSave}
                                />
                            )}
                        </SpacingColumn>
                    </SpacingColumn>
                </Background>
            </MinHeight>
        </BackgroundColour>
    );
};
